import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Briefcase, RefreshCw, AlertCircle, ToggleLeft, ToggleRight, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminJobService, { AdminJob } from '../../services/admin/jobService';
import DataTable from '../../components/common/DataTable';

const JobsManagement: React.FC = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<AdminJob[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [deleteModal, setDeleteModal] = useState<{ show: boolean; job: AdminJob | null }>({
        show: false,
        job: null
    });
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async (showToast = false) => {
        try {
            if (showToast) setRefreshing(true);
            else setLoading(true);

            const data = await adminJobService.getAllJobs();
            setJobs(data);

            if (showToast) toast.success('Jobs refreshed!');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch jobs');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleToggleStatus = async (job: AdminJob) => {
        try {
            await adminJobService.toggleJobStatus(job.id);
            toast.success(`Job ${job.is_active ? 'disabled' : 'enabled'} successfully!`);
            fetchJobs();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update job status');
        }
    };

    const handleDeleteJob = async () => {
        if (!deleteModal.job) return;
        try {
            setDeleting(true);
            await adminJobService.deleteJob(deleteModal.job.id);
            toast.success('Job deleted successfully!');
            setDeleteModal({ show: false, job: null });
            fetchJobs();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete job');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-secondary-900">Jobs Management</h1>
                    <p className="text-secondary-600 mt-1">Manage job openings and track applications</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('/admin/job-applications')}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <Users size={18} />
                        View Applications
                    </button>
                    <button
                        onClick={() => fetchJobs(true)}
                        disabled={refreshing}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                    <button
                        onClick={() => navigate('/admin/jobs/create')}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 font-bold"
                    >
                        <Plus size={18} />
                        Post New Job
                    </button>
                </div>
            </div>

            {/* Jobs Table */}
            <DataTable
                data={jobs}
                loading={loading}
                searchable={true}
                searchPlaceholder="Search jobs by title, location, or department..."
                searchFields={['title', 'location', 'department']}
                filterable={true}
                filterOptions={[
                    { label: 'All Jobs', value: 'all', field: 'is_active' },
                    { label: 'Active', value: 'true', field: 'is_active' },
                    { label: 'Disabled', value: 'false', field: 'is_active' },
                ]}
                columns={[
                    {
                        header: 'Job Title',
                        accessor: (job) => (
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${job.is_active ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'}`}>
                                    <Briefcase size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-secondary-900">{job.title}</h3>
                                    <p className="text-xs text-secondary-500">{job.department} • {job.employment_type}</p>
                                </div>
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Location',
                        accessor: (job) => <span className="text-sm font-medium">{job.location}</span>,
                        sortable: true,
                    },
                    {
                        header: 'Applications',
                        accessor: (job) => (
                            <button 
                                onClick={(e) => { e.stopPropagation(); navigate(`/admin/job-applications?job_id=${job.id}`); }}
                                className="inline-flex items-center gap-2 px-3 py-1 bg-secondary-50 text-secondary-700 rounded-full text-xs font-bold hover:bg-secondary-100 transition-all border border-secondary-100"
                            >
                                <Users size={14} />
                                {job.application_count} Applications
                            </button>
                        ),
                        align: 'center',
                        sortable: true,
                    },
                    {
                        header: 'Status',
                        accessor: (job) => (
                            <span className={`px-3 py-1 text-xs rounded-full font-bold uppercase tracking-wider ${job.is_active ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                                {job.is_active ? 'Active' : 'Disabled'}
                            </span>
                        ),
                        align: 'center',
                    },
                    {
                        header: 'Created On',
                        accessor: (job) => <span className="text-sm text-secondary-500">{new Date(job.created_at).toLocaleDateString()}</span>,
                        align: 'center',
                        sortable: true,
                    }
                ]}
                renderActions={(job) => (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(job); }}
                            className={`p-2 rounded-lg transition-colors ${job.is_active ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-gray-400 bg-gray-50 hover:bg-gray-100'}`}
                            title={job.is_active ? 'Disable Job' : 'Enable Job'}
                        >
                            {job.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/jobs/${job.id}/edit`); }}
                            className="p-2 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                            title="Edit Job"
                        >
                            <Edit2 size={18} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); setDeleteModal({ show: true, job }); }}
                            className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Job"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                )}
            />

            {/* Delete Modal */}
            {deleteModal.show && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden animate-scale-in">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                <AlertCircle size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-secondary-900 mb-2">Delete Job</h3>
                            <p className="text-secondary-600 mb-8 leading-relaxed">
                                Are you sure you want to delete <strong>{deleteModal.job?.title}</strong>? This will also remove all submitted applications for this job.
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setDeleteModal({ show: false, job: null })}
                                    className="flex-1 px-6 py-3 bg-gray-100 text-secondary-700 font-bold rounded-xl hover:bg-gray-200 transition-all font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteJob}
                                    disabled={deleting}
                                    className="flex-1 px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {deleting ? 'Deleting...' : 'Delete Job'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobsManagement;
